"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import React from "react";

import { useStaticGifObjectUrl } from "@/shared/lib/hooks/use-static-gif-object-url";
import { isLikelyAnimatedGifUrl } from "@/shared/lib/image-utils";

type SafeBannerProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  rounded?: string;
  onError?: () => void;
  onLoad?: () => void;
  allowAnimatedGif?: boolean;
  /** Когда баннера нет/не загрузился — показывать фирменный бренд-градиент с белым
   *  GL-знаком (как в дизайн-системе) вместо слабого from-primary/10. Включаем точечно
   *  на обложках турниров; в остальных местах остаётся прежний лёгкий фолбэк. */
  brandFallback?: boolean;
  /** Баннер — LCP-элемент маршрута: грузить сразу и с высоким приоритетом.
   *  Ставить строго на один баннер на страницу, иначе теряется смысл (KAN-977). */
  priority?: boolean;
};

export function SafeBanner({
  src,
  alt,
  className = "",
  fill = true,
  width,
  height,
  rounded = "rounded-lg",
  onError,
  onLoad,
  allowAnimatedGif = false,
  brandFallback = false,
  priority = false,
}: SafeBannerProps) {
  const t = useTranslations("Common.media");
  const altText = alt ?? t("cover");
  const [errored, setErrored] = React.useState(false);

  const normalizedSrc =
    typeof src === "string" && src.trim() !== "" ? src.trim() : null;

  const isExternalUrl =
    typeof normalizedSrc === "string" &&
    (normalizedSrc.startsWith("http://") || normalizedSrc.startsWith("https://"));
  const useNativeImg =
    typeof normalizedSrc === "string" &&
    (normalizedSrc.includes("/uploads/") ||
      normalizedSrc.startsWith("http://localhost/") ||
      normalizedSrc.startsWith("http://127.0.0.1/") ||
      normalizedSrc.startsWith("https://localhost/") ||
      normalizedSrc.startsWith("https://127.0.0.1/") ||
      isExternalUrl);
  const showImage =
    normalizedSrc &&
    !errored &&
    !normalizedSrc.includes("/default-cover.svg");

  const wantsStaticGif =
    Boolean(
      showImage &&
        normalizedSrc &&
        !allowAnimatedGif &&
        isLikelyAnimatedGifUrl(normalizedSrc),
    );

  const { url: staticGifUrl, pending: staticGifPending } =
    useStaticGifObjectUrl(normalizedSrc, wantsStaticGif);

  const effectiveSrc =
    wantsStaticGif && staticGifPending
      ? null
      : wantsStaticGif
        ? (staticGifUrl ?? normalizedSrc)
        : normalizedSrc;

  const gifAnimated =
    Boolean(
      effectiveSrc &&
        allowAnimatedGif &&
        isLikelyAnimatedGifUrl(effectiveSrc),
    );

  // Наши видео-баннеры (mp4 → анимированный webp) через next/image перезапускались на
  // каждом ре-рендере (инлайновый loader) — анимация «дёргалась» каждые пол-секунды.
  // Рендерим их обычным <img> (как анимированные GIF) — стабильный элемент не сбрасывает
  // проигрывание. Только в fill-режиме (баннеры), где стили absolute inset-0 подходят.
  const isNativeAnimatedWebp =
    Boolean(
      showImage &&
        effectiveSrc &&
        fill &&
        useNativeImg &&
        (effectiveSrc.toLowerCase().split("?")[0] ?? "").endsWith(".webp"),
    );
  const usePlainImg = gifAnimated || isNativeAnimatedWebp;

  const isBlobUrl =
    typeof effectiveSrc === "string" && effectiveSrc.startsWith("blob:");

  return (
    <div
      className={`relative overflow-hidden w-full h-full ${rounded} ${className}`}
    >
      {usePlainImg && effectiveSrc ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError это событие загрузки картинки, а не взаимодействие пользователя
        <img
          src={effectiveSrc}
          alt={altText}
          loading={priority ? "eager" : undefined}
          fetchPriority={priority ? "high" : undefined}
          className="absolute inset-0 z-0 h-full w-full object-cover"
          onError={() => {
            setErrored(true);
            onError?.();
          }}
          onLoad={() => onLoad?.()}
        />
      ) : showImage && effectiveSrc ? (
        <Image
          src={effectiveSrc}
          alt={altText}
          fill={fill}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          sizes={fill ? "100vw" : undefined}
          loading={priority ? "eager" : undefined}
          fetchPriority={priority ? "high" : undefined}
          className={
            fill ? "absolute inset-0 h-full w-full object-cover z-0" : "object-cover z-0"
          }
          loader={useNativeImg || isBlobUrl ? ({ src }) => src : undefined}
          unoptimized={useNativeImg || isBlobUrl}
          onError={() => {
            setErrored(true);
            onError?.();
          }}
          onLoad={() => onLoad?.()}
        />
      ) : null}

      {showImage && wantsStaticGif && staticGifPending && (
        <div className="absolute inset-0 z-0 bg-linear-to-r from-primary/10 to-transparent dark:from-primary/20" />
      )}

      {!showImage &&
        (brandFallback ? (
          <div
            className="absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(120deg, oklch(0.27 0 0), oklch(0.145 0 0) 72%)",
            }}
          >
            <div className="absolute inset-0 grid place-items-center">
              <img
                src="/logo/logowhite.svg"
                alt=""
                aria-hidden
                className="w-[22%] min-w-[36px] max-w-[112px] opacity-15"
              />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 z-0 bg-linear-to-r from-primary/10 to-transparent dark:from-primary/20" />
        ))}
    </div>
  );
}
